import React, { useState, useEffect, useContext } from "react";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useQuery } from "@tanstack/react-query";
import Bread from "../../common/Bread";
import axios from "axios";
import Loader from "../../common/Loader";
import { LangContext } from "../../context/LangContext";
import "./forex.scss";

const StyledHeadTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#003399",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    fontFamily: "DM Sans, sans-serif",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#ADD8E6",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    fontFamily: "DM Sans, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(173, 216, 230, 0.4)",
  },
}));

const Forex = () => {
  const lang = useContext(LangContext);
  const nep = lang.state.np; // true if Nepali is active

  const [value, setValue] = useState(null);
  const [isFetch, setIsFetch] = useState(false);
  const [locData, setLocData] = useState();

  const breadcrumList = {
    image: "",
    title: nep ? "विदेशी मुद्रा दर" : "Forex",
    l1: nep ? "होम" : "Home",
    l2: nep ? "विदेशी मुद्रा दर" : "Forex",
    l3: "",
  };

  const handleChange = (newValue) => {
    if (!newValue) return;

    const AMonth = newValue.$M + 1;
    const MDate = `${AMonth < 10 ? "0" + AMonth : AMonth}`;
    const MDay = `${newValue.$D < 10 ? "0" + newValue.$D : newValue.$D}`;
    const SDate = `${newValue.$y}-${MDate}-${MDay}`;
    setValue(SDate);
  };

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ["forexData", value, isFetch],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/forex/getForex/${
            value === null ? "" : value
          }`,
        )
        .then((res) => res.data),
  });

  useEffect(() => {
    if (data?.data) {
      setLocData(data);
    } else {
      setIsFetch(!isFetch);
    }
  }, [data]);

  if (isFetching || isLoading || !locData) return <Loader />;
  if (error) return "An error has occurred: " + error.message;

  return (
    <Bread data={breadcrumList}>
      <div className="forexContainer">
        <div className="forexPagesHeaderFlexContainer">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {window.screen.width > 600 ? (
              <DesktopDatePicker
                label={nep ? "मिति" : "Date"}
                inputFormat="YYYY-MM-DD"
                value={value}
                onChange={handleChange}
                disableFuture={true}
                renderInput={(params) => <TextField {...params} />}
                className="mui-date"
              />
            ) : (
              <MobileDatePicker
                label={nep ? "मिति" : "Date"}
                inputFormat="YYYY-MM-DD"
                value={value}
                onChange={handleChange}
                disableFuture={true}
                renderInput={(params) => <TextField {...params} />}
                className="mui-date"
              />
            )}
          </LocalizationProvider>

          <h3>
            {nep ? "अद्यावधिक: " : "Updated on: "}{" "}
            {data.status === "success" &&
              data.data[0].published_on.substring(0, 10)}
          </h3>
        </div>

        {data.status === "success" ? (
          <div className="InterestPageTableContainer">
            <TableContainer
              sx={{ border: "1px solid #c2c2c2" }}
              component={Paper}
            >
              <Table sx={{ minWidth: 500 }} aria-label={nep ? "विदेशी मुद्रा दर तालिका" : "Foreign exchange rates table"}>
                <caption className="a11y-sr-only">
                  {nep ? "विदेशी मुद्रा दर" : "Foreign exchange rates"}
                </caption>
                <TableHead>
                  <TableRow>
                    <StyledHeadTableCell align="center" scope="col">
                      {nep ? "क्र.सं." : "S.No"}
                    </StyledHeadTableCell>
                    <StyledHeadTableCell align="center" scope="col">
                      {nep ? "देश" : "Country"}
                    </StyledHeadTableCell>
                    <StyledHeadTableCell align="center" scope="col">
                      {nep ? "एकाइ" : "Unit"}
                    </StyledHeadTableCell>
                    <StyledHeadTableCell align="center" scope="col">
                      {nep ? "किन्ने" : "Buy"}
                    </StyledHeadTableCell>
                    <StyledHeadTableCell align="center" scope="col">
                      {nep ? "बिक्री" : "Sell"}
                    </StyledHeadTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.data[0]?.forex_values.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell align="center" scope="row">
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <div className="flag-code-container">
                          <div
                            className={`currency-flag currency-flag-${item.iso3}`}
                            role="presentation"
                            aria-hidden="true"
                          ></div>
                          <p>{item.iso3}</p>
                        </div>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item.unit}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item.buy}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item.sell}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ) : (
          <h3>{nep ? "डेटा उपलब्ध छैन" : "No data"}</h3>
        )}
      </div>
    </Bread>
  );
};

export default Forex;
